import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Psychology as MLIcon,
  Security as PrivacyIcon,
  Rule as RuleIcon,
  Analytics as AnalyticsIcon,
  Timeline as TimelineIcon,
  TrendingUp as TrendingIcon,
  Info as InfoIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import RuleEditor from '../components/context/RuleEditor';
import PrivacySettings from '../components/context/PrivacySettings';
import ActivityChart from '../components/context/ActivityChart';
import FocusMetrics from '../components/context/FocusMetrics';
import PredictionPanel from '../components/context/PredictionPanel';
import { contextAPI } from '../services/api/context';
import { ContextState, ActivityAnalysis, MLModel } from '../types/context';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`context-tabpanel-${index}`}
      aria-labelledby={`context-tab-${index}`}
      {...other}
    >
      {value === index && <div style={{ padding: 24 }}>{children}</div>}
    </div>
  );
}

const ContextDashboard: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [contextState, setContextState] = useState<ContextState | null>(null);
  const [activityAnalysis, setActivityAnalysis] = useState<ActivityAnalysis | null>(null);
  const [mlModels, setMLModels] = useState<MLModel[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [stateRes, analysisRes, modelsRes] = await Promise.all([
        contextAPI.getCurrentState(),
        contextAPI.getActivityAnalysis({ period: '24h' }),
        contextAPI.getMLModels(),
      ]);

      setContextState(stateRes.data.state);
      setActivityAnalysis(analysisRes.data);
      setMLModels(modelsRes.data.models);
      setError(null);
    } catch (err: any) {
      setError('Failed to load dashboard data');
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (!user) {
    return <Alert severity="error">Please log in to access the context dashboard</Alert>;
  }

  return (
    <Container maxWidth="xl">
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Typography variant="h3">Context Dashboard</Typography>
          <IconButton onClick={loadDashboardData} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </div>
        
        {loading && <LinearProgress />}
        {error && <Alert severity="error" style={{ marginBottom: 16 }}>{error}</Alert>}

        {/* Overview Cards */}
        <Grid container spacing={3} style={{ marginBottom: 24 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <Typography color="textSecondary" gutterBottom>
                      Current Activity
                    </Typography>
                    <Typography variant="h5">
                      {contextState?.activity.type || 'Unknown'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {contextState && (
                        <Chip
                          label={`${Math.round(contextState.activity.confidence * 100)}% confidence`}
                          size="small"
                          color="primary"
                        />
                      )}
                    </Typography>
                  </div>
                  <AnalyticsIcon color="primary" style={{ fontSize: 40 }} />
                </div>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <Typography color="textSecondary" gutterBottom>
                      Focus Level
                    </Typography>
                    <Typography variant="h5">
                      {contextState ? `${Math.round(contextState.focus.level * 100)}%` : 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {contextState && `${contextState.focus.duration} min session`}
                    </Typography>
                  </div>
                  <TimelineIcon color="secondary" style={{ fontSize: 40 }} />
                </div>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <Typography color="textSecondary" gutterBottom>
                      Total Events
                    </Typography>
                    <Typography variant="h5">
                      {activityAnalysis?.summary.total_events || 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Last 24 hours
                    </Typography>
                  </div>
                  <TrendingIcon color="success" style={{ fontSize: 40 }} />
                </div>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <Typography color="textSecondary" gutterBottom>
                      ML Models
                    </Typography>
                    <Typography variant="h5">
                      {mlModels.filter(m => m.status === 'active').length}/{mlModels.length}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Active models
                    </Typography>
                  </div>
                  <MLIcon color="info" style={{ fontSize: 40 }} />
                </div>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabbed Content */}
        <Paper>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Overview" icon={<InfoIcon />} iconPosition="start" />
            <Tab label="Activity Analysis" icon={<AnalyticsIcon />} iconPosition="start" />
            <Tab label="Adaptation Rules" icon={<RuleIcon />} iconPosition="start" />
            <Tab label="Privacy Settings" icon={<PrivacyIcon />} iconPosition="start" />
            <Tab label="ML Models" icon={<MLIcon />} iconPosition="start" />
            <Tab label="Predictions" icon={<TrendingIcon />} iconPosition="start" />
          </Tabs>

          <TabPanel value={activeTab} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Paper style={{ padding: 16, marginBottom: 16 }}>
                  <Typography variant="h6" gutterBottom>
                    Activity Timeline
                  </Typography>
                  <ActivityChart data={[]} />
                </Paper>
                <Paper style={{ padding: 16 }}>
                  <Typography variant="h6" gutterBottom>
                    Focus Metrics
                  </Typography>
                  <FocusMetrics 
                    focusScore={0}
                    deepWorkHours={0}
                    interruptionCount={0}
                    productivityIndex={0}
                  />
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper style={{ padding: 16 }}>
                  <Typography variant="h6" gutterBottom>
                    Recent Patterns
                  </Typography>
                  <List>
                    {activityAnalysis?.patterns.slice(0, 5).map((pattern, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <Chip
                            label={`${Math.round(pattern.confidence * 100)}%`}
                            size="small"
                            color={pattern.confidence > 0.8 ? 'success' : 'default'}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={pattern.type}
                          secondary={`${pattern.frequency} times, ~${pattern.duration} min`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <ActivityChart data={[]} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper style={{ padding: 16 }}>
                  <Typography variant="h6" gutterBottom>
                    Activity Insights
                  </Typography>
                  <List>
                    {activityAnalysis?.insights.map((insight, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={insight.description}
                          secondary={insight.recommendation}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper style={{ padding: 16 }}>
                  <Typography variant="h6" gutterBottom>
                    Peak Hours
                  </Typography>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {activityAnalysis?.summary.peak_hours.map((hour) => (
                      <Chip
                        key={hour}
                        label={`${hour}:00`}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </div>
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <RuleEditor userId={user.id} organizationId={user.organizationId || ''} />
          </TabPanel>

          <TabPanel value={activeTab} index={3}>
            <PrivacySettings userId={user.id} />
          </TabPanel>

          <TabPanel value={activeTab} index={4}>
            <Grid container spacing={3}>
              {mlModels.map((model) => (
                <Grid item xs={12} md={6} key={model.id}>
                  <Card>
                    <CardContent>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <div>
                          <Typography variant="h6">{model.name}</Typography>
                          <Typography color="textSecondary" gutterBottom>
                            {model.type} - v{model.version}
                          </Typography>
                          <div style={{ marginTop: 8 }}>
                            <Chip
                              label={model.status}
                              color={model.status === 'active' ? 'success' : 'default'}
                              size="small"
                              style={{ marginRight: 8 }}
                            />
                            <Chip
                              label={`${Math.round(model.accuracy * 100)}% accuracy`}
                              size="small"
                            />
                          </div>
                          <Typography variant="body2" style={{ marginTop: 8 }}>
                            Last trained: {new Date(model.last_trained).toLocaleDateString()}
                          </Typography>
                          <Typography variant="body2">
                            Training samples: {model.training_samples.toLocaleString()}
                          </Typography>
                        </div>
                        <IconButton size="small">
                          <SettingsIcon />
                        </IconButton>
                      </div>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </TabPanel>

          <TabPanel value={activeTab} index={5}>
            <PredictionPanel predictions={[]} />
          </TabPanel>
        </Paper>
      </div>
    </Container>
  );
};

export default ContextDashboard;